import { Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import Form from "components/form/Form";
import TextFieldControl from "components/form/TextFieldControl";
import { useContext, useState } from "react";
import useIsCreate from "hooks/useIsCreate";
import {
  CreateVacancyInput,
  useCreateVacancyMutation,
  useDeleteVacancyMutation,
  useVacancyQuery,
} from "@generated/graphql";
import { useNavigate, useParams } from "react-router-dom";
import VALIDATION from "helpers/validation";
import SaveEditButtons from "components/buttons/SaveEditButtons";
import { ConfirmModalContext } from "context/confirmModalContext";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import ROUTES from "constants/routes";
import { parseApolloError } from "helpers/errorsParser";

const VacancyForm = () => {
  const { vacancyId } = useParams();
  const isCreate = useIsCreate();
  const [isEditing, setIsEditing] = useState(isCreate);
  const { handleOpen } = useContext(ConfirmModalContext);
  const { addToast } = useContext(ToastContext);
  const navigate = useNavigate();
  const { data } = useVacancyQuery({ variables: { vacancyId: vacancyId! } });
  const [createVacancy, { loading: createVacancyLoading }] = useCreateVacancyMutation();
  const [deleteVacancy, { loading: deleteVacancyLoading }] = useDeleteVacancyMutation();

  const vacancy = data?.vacancy;

  const form = useForm<CreateVacancyInput>({
    values: {
      title: vacancy?.title || "",
      description: vacancy?.description || "",
    },
  });

  const { handleSubmit } = form;

  const onCreateVacancy = (formData: CreateVacancyInput) => {
    createVacancy({
      variables: {
        input: formData,
      },
    })
      .then((res) => {
        if (isCreate) navigate(`/${ROUTES.VACANCIES}/view/${res.data?.createVacancy.id}`, { replace: true });
        setIsEditing(false);
        addToast({ type: ToastTypeEnum.SUCCESS });
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  const handleDeleteVacancy = async () => {
    await deleteVacancy({ variables: { vacancyId: vacancyId! } })
      .then(() => {
        addToast({ type: ToastTypeEnum.SUCCESS, text: "Вакансия успешно удалена" });
        navigate(`/${ROUTES.VACANCIES}`);
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  const onDeleteVacancy = () => {
    handleOpen({
      title: "Вы уверены, что хотите удалить вакансию?",
      actionButtonText: "Удалить",
      handleClick: handleDeleteVacancy,
    });
  };

  return (
    <Form form={form}>
      <Stack spacing={2}>
        <TextFieldControl
          name='title'
          label='Название'
          placeholder='Название'
          disabled={!isCreate}
          rules={{ required: true, ...VALIDATION.competencyMapTitle }}
        />
        <TextFieldControl
          name='description'
          label='Описание'
          placeholder='Описание'
          disabled={!isCreate}
          multiline
          minRows={4}
          maxRows={4}
        />
        <Stack
          sx={{ width: 256 }}
          spacing={2}>
          <SaveEditButtons
            isEdited={isEditing}
            isEditable={false}
            onSubmit={handleSubmit(onCreateVacancy)}
            setIsEdited={setIsEditing}
            loading={createVacancyLoading || deleteVacancyLoading}
            deleteText='Удалить вакансию'
            onDelete={onDeleteVacancy}
          />
        </Stack>
      </Stack>
    </Form>
  );
};

export default VacancyForm;
